export interface ClientContact {
    id: string;
    name: string;
    phoneNumber: string;
    email: string;
    description: string;
}

export interface ClientChangeHistoryEntry {
    id: string;
    createdAt: string;
    source: string;
    title: string;
    detail?: string;
}

export interface ClientNotesData {
    publicPhoneNumber: string;
    contacts: ClientContact[];
    notes: string;
    changeHistory: ClientChangeHistoryEntry[];
}

export const CLIENT_NOTES_SCHEMA = "voicify.controls.clientNotes";
export const MAX_CLIENT_CHANGE_HISTORY_ENTRIES = 30;
export const RETURN_TO_STORE_NUMBER_VARIABLES = [
    "{{session.returntostorenumber}}",
    "{session.returntostorenumber}",
    "session.returntostorenumber",
    "{TransferTarget}",
    "TransferTarget"
];

export const createContactId = (): string => `contact-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const createChangeHistoryId = (): string => `change-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const createEmptyContact = (): ClientContact => ({
    id: createContactId(),
    name: "",
    phoneNumber: "",
    email: "",
    description: ""
});

export const createEmptyNotesData = (): ClientNotesData => ({
    publicPhoneNumber: "",
    contacts: [],
    notes: "",
    changeHistory: []
});

export const normalizeContact = (contact: any): ClientContact => ({
    id: contact?.id || createContactId(),
    name: String(contact?.name || ""),
    phoneNumber: String(contact?.phoneNumber || contact?.phone || contact?.number || ""),
    email: String(contact?.email || ""),
    description: String(contact?.description || contact?.notes || "")
});

export const normalizeChangeHistoryEntry = (entry: any): ClientChangeHistoryEntry | null => {
    const createdAt = String(entry?.createdAt || entry?.date || "");
    const source = String(entry?.source || "");
    const title = String(entry?.title || entry?.summary || "");

    if (!createdAt || !source || !title) {
        return null;
    }

    return {
        id: String(entry?.id || createChangeHistoryId()),
        createdAt,
        source,
        title,
        detail: entry?.detail ? String(entry.detail) : undefined
    };
};

export const parseClientNotes = (value?: string): ClientNotesData => {
    const rawValue = value || "";
    if (!rawValue.trim()) {
        return createEmptyNotesData();
    }

    try {
        const parsed = JSON.parse(rawValue);
        if (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed) &&
            parsed.schema === CLIENT_NOTES_SCHEMA &&
            parsed.version === 1
        ) {
            return {
                publicPhoneNumber: String(parsed.publicPhoneNumber || parsed.publicForwardingPhoneNumber || ""),
                contacts: Array.isArray(parsed.contacts) ? parsed.contacts.map(normalizeContact) : [],
                notes: String(parsed.notes || parsed.additionalNotes || ""),
                changeHistory: Array.isArray(parsed.changeHistory)
                    ? parsed.changeHistory
                          .map(normalizeChangeHistoryEntry)
                          .filter(Boolean)
                          .slice(0, MAX_CLIENT_CHANGE_HISTORY_ENTRIES) as ClientChangeHistoryEntry[]
                    : []
            };
        }
    } catch {
        // Existing client context values may be plain notes. Preserve them as additional notes.
    }

    return {
        ...createEmptyNotesData(),
        notes: rawValue
    };
};

export const isNotesDataEmpty = (notesData: ClientNotesData): boolean => {
    return !notesData.publicPhoneNumber.trim() &&
        !notesData.notes.trim() &&
        notesData.changeHistory.length === 0 &&
        !notesData.contacts.some(contact => (
            contact.name.trim() ||
            contact.phoneNumber.trim() ||
            contact.email.trim() ||
            contact.description.trim()
        ));
};

export const serializeClientNotes = (notesData: ClientNotesData): string => {
    if (isNotesDataEmpty(notesData)) {
        return "";
    }

    return JSON.stringify({
        schema: CLIENT_NOTES_SCHEMA,
        version: 1,
        publicPhoneNumber: notesData.publicPhoneNumber.trim(),
        contacts: notesData.contacts
            .map(contact => ({
                name: contact.name.trim(),
                phoneNumber: contact.phoneNumber.trim(),
                email: contact.email.trim(),
                description: contact.description.trim()
            }))
            .filter(contact => contact.name || contact.phoneNumber || contact.email || contact.description),
        notes: notesData.notes,
        changeHistory: notesData.changeHistory
            .slice(0, MAX_CLIENT_CHANGE_HISTORY_ENTRIES)
            .map(entry => ({
                id: entry.id,
                createdAt: entry.createdAt,
                source: entry.source,
                title: entry.title,
                detail: entry.detail
            }))
    }, null, 2);
};

export const appendClientChangeHistory = (
    currentValue: string,
    event: { source: string; title: string; detail?: string; createdAt?: string; }
): string => {
    const notesData = parseClientNotes(currentValue);
    const nextEntry: ClientChangeHistoryEntry = {
        id: createChangeHistoryId(),
        createdAt: event.createdAt || new Date().toISOString(),
        source: event.source,
        title: event.title,
        detail: event.detail
    };

    return serializeClientNotes({
        ...notesData,
        changeHistory: [nextEntry].concat(notesData.changeHistory).slice(0, MAX_CLIENT_CHANGE_HISTORY_ENTRIES)
    });
};

export const getClientNotesMeta = (value: string): string => {
    const notesData = parseClientNotes(value);
    const contactCount = notesData.contacts.filter(contact => (
        contact.name.trim() ||
        contact.phoneNumber.trim() ||
        contact.email.trim() ||
        contact.description.trim()
    )).length;
    const parts = [];

    if (notesData.publicPhoneNumber.trim()) {
        parts.push("Public number");
    }
    if (contactCount > 0) {
        parts.push(contactCount === 1 ? "1 contact" : `${contactCount} contacts`);
    }
    if (notesData.notes.trim()) {
        parts.push("Notes");
    }
    if (notesData.changeHistory.length > 0) {
        parts.push("History");
    }

    return parts.length ? parts.join(" / ") : "Add notes";
};

export const isReturnToStoreNumberVariable = (associatedVariable?: string): boolean => {
    const normalizedVariable = (associatedVariable || "").trim().toLowerCase();
    return RETURN_TO_STORE_NUMBER_VARIABLES.some(variable => variable.toLowerCase() === normalizedVariable);
};

export const getReturnToStoreNumberFromFields = (
    fields: Array<{ associatedVariable?: string; value?: string; currentValue?: string; defaultValue?: string; }> = []
): string => {
    const field = fields.find(candidate => isReturnToStoreNumberVariable(candidate.associatedVariable));
    const value = field?.currentValue || field?.value || "";

    if (!value || value === field?.associatedVariable || value === field?.defaultValue) {
        return "";
    }

    return value;
};
